import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import {
    IMessageElement,
    sideViewState,
    threadHistoryState,
    useChatMessages,
    useConfig
} from '@chainlit/react-client';
import { useApi } from '@chainlit/react-client';
import type { IThread } from '@chainlit/react-client';

import AutoResumeThread from '@/components/AutoResumeThread';
import { ReadOnlyThread } from '@/components/ReadOnlyThread';
import Chat from '@/components/chat';
import ThreadSkeleton from '@/components/chat/ThreadSkeleton';

export default function ThreadPage() {
    const { id } = useParams();
    const { config } = useConfig();

    const setThreadHistory = useSetRecoilState(threadHistoryState);
    const setSideView = useSetRecoilState(sideViewState);

    const { threadId } = useChatMessages();

    const [refreshKey, setRefreshKey] = useState(0);

    // Listen for thread pin/unpin events to reload the thread
    useEffect(() => {
        const handleThreadPinChange = (event: CustomEvent) => {
            if (event.detail.threadId === id) {
                setRefreshKey((prev) => prev + 1);
            }
        };

        window.addEventListener(
            'thread-pin-changed' as any,
            handleThreadPinChange
        );

        return () => {
            window.removeEventListener(
                'thread-pin-changed' as any,
                handleThreadPinChange
            );
        };
    }, [id]);

    // Listen for file upload events to reload the thread data
    useEffect(() => {
        const handleFileUploaded = (event: CustomEvent) => {
            // Refresh thread data when a file is uploaded
            if (!event.detail?.threadId || event.detail.threadId === id) {
                setRefreshKey((prev) => prev + 1);
            }
        };

        window.addEventListener('file-uploaded' as any, handleFileUploaded);

        return () => {
            window.removeEventListener(
                'file-uploaded' as any,
                handleFileUploaded
            );
        };
    }, [id]);

    const { data: thread } = useApi<IThread>(
        id ? `/project/thread/${id}?_refresh=${refreshKey}` : null,
        {
            revalidateOnFocus: false
        }
    );

    useEffect(() => {
        if (threadId !== id) setSideView(undefined);
    }, [id]);

    const isCurrentThread = threadId === id;
    const isPinnedThread = thread?.isPinned === true;

    // Merge thread elements into sideView when thread data changes.
    // Uses functional update to preserve elements that arrived via socket
    // events but may not yet be persisted in the thread API response.
    useEffect(() => {
        if (thread?.elements && thread.elements.length > 0) {
            setSideView((prev) => {
                const threadElements = thread.elements as IMessageElement[];
                const threadIds = new Set(threadElements.map((e) => e.id));
                const preserved = (prev?.elements || []).filter(
                    (e) => !threadIds.has(e.id)
                );
                return {
                    title: prev?.title || 'Charts',
                    elements: [...threadElements, ...preserved]
                };
            });
        }
    }, [thread?.elements, setSideView]);

    useEffect(() => {
        setThreadHistory((prev) => {
            if (prev?.currentThreadId === id) return prev;
            return { ...prev, currentThreadId: id };
        });
    }, [id]);

    return (
        <>
            {config?.threadResumable && !isCurrentThread && !isPinnedThread ? (
                <AutoResumeThread id={id!} />
            ) : null}
            {config?.threadResumable ? (
                isCurrentThread && !isPinnedThread ? (
                    <Chat />
                ) : isPinnedThread ? (
                    thread ? (
                        <ReadOnlyThread id={id!} />
                    ) : (
                        <ThreadSkeleton />
                    )
                ) : (
                    <ThreadSkeleton />
                )
            ) : null}
        </>
    );
}
